import { memo, useState } from 'react'

import { Debtor } from 'containers/PersonalForm/PersonalForm.interface'
import { Unit } from 'components/Input/Text'
import PathAmountInput from 'components/PathAmountInput'
import isRetired from 'utils/formDependencies/conditions/isRetired'
import { Box, IconButton, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/Delete'

import { getHighestRetirementPension } from './utils'
import DeleteDebtorDialog from './DeleteDebtorDialog'
import { observer } from 'mobx-react'
import { getCookie, COOKIE_KEY_FCID } from 'utils/cookie'
import { trackTeal } from 'utils/tracking'
import { useStores } from 'stores/utils/hooks/useStores'
import { AFFILIATE_ID_SMAVA } from 'utils/affiliate'

const RCEEditFieldDebtor = observer(
  ({
    debtor = Debtor.PRIMARY,
    outerLabel = false,
    smallTextField = false,
    handleOnlyPrimaryDebtor = false
  }) => {
    const isPensionerOrDebtor = isRetired(debtor)
    const [showDeleteModal, toggleDeleteModal] = useState(false)
    const {
      traversal: {
        data,
        data: {
          debtors,
          debtors: {
            primary: { personId }
          },
          system: { subId, refId, configurationId, advertisementId }
        }
      }
    } = useStores()

    // the logic below should be done via formDependencies.tsx if the test is successful or at CAT-4118 cleanup
    const showPensionField = isPensionerOrDebtor(data)
    const pensions = showPensionField ? debtors[debtor].income.pensions : []
    const hasPensionOrRentner = getHighestRetirementPension(pensions || [])
    const isDebtorCountTwo = debtors.count === 2 && !handleOnlyPrimaryDebtor

    const onBlur = (value: number | undefined) => {
      if (value !== undefined) {
        trackTeal({
          transaction: {
            event: 'edit_page_rente_changed'
          },
          affiliateId: AFFILIATE_ID_SMAVA,
          subId,
          refId,
          configurationId,
          traversalPersonId: personId,
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            userAttributes: {
              advertisementId
            }
          }
        })
      }
    }

    return (
      <>
        <Box
          sx={
            isDebtorCountTwo
              ? {
                  border: `1px solid #BFBFBF`,
                  padding: '16px',
                  borderRadius: '8px'
                }
              : {}
          }
        >
          {isDebtorCountTwo && (
            <>
              <Typography variant="body2">{`${debtor === Debtor.PRIMARY ? 1 : 2}. KREDITNEHMER:`}</Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: '700',
                  marginBottom: '24px',
                  textTransform: 'capitalize'
                }}
              >{`${debtors[debtor].personal.firstname} ${debtors[debtor].personal.lastname}`}</Typography>
            </>
          )}
          <PathAmountInput
            path={`debtors.${debtor}.income.primary`}
            unit={Unit.EURO_MONTH}
            showTooltipOnFieldClick
            touchIgnored
            outerLabel={outerLabel}
            smallTextField={smallTextField}
          />
          {hasPensionOrRentner && (
            <PathAmountInput
              path={`debtors.${debtor}.income.pensions.${[hasPensionOrRentner.index]}.income`}
              unit={Unit.EURO_MONTH}
              showTooltipOnFieldClick
              touchIgnored
              label="Rente (netto)"
              defaultValue={hasPensionOrRentner.income}
              onBlur={onBlur}
              outerLabel={outerLabel}
              smallTextField={smallTextField}
            />
          )}
          {debtor === Debtor.SECONDARY && (
            <Box
              textAlign="left"
              onClick={() => toggleDeleteModal(!showDeleteModal)}
              sx={{
                cursor: 'pointer', // Optional for UX
                display: 'flex',
                justifyContent: 'right',
                marginTop: '16px',
                '&:hover': {
                  color: '#6D1C1C'
                },
                '&:hover .hover-target': {
                  color: '#6D1C1C'
                }
              }}
            >
              <IconButton
                disableRipple
                color="error"
                sx={{ padding: `0px !important` }}
              >
                <DeleteOutlineIcon className="hover-target" />
                <Typography
                  className="hover-target"
                  variant="body2"
                  color="error"
                  fontWeight={700}
                >
                  diesen Kreditnehmer entfernen
                </Typography>
              </IconButton>
            </Box>
          )}
          <DeleteDebtorDialog
            showDeleteModal={showDeleteModal}
            hideDeleteModal={() => toggleDeleteModal(false)}
          />
        </Box>
      </>
    )
  }
)

export default memo(RCEEditFieldDebtor)
